import {
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import {
  Loader2,
  Eye,
  EyeOff,
  ShieldCheck,
  CheckCircle2,
  XCircle,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { makeRequest } from "@/utils/makeRequest";
import { toast } from "sonner";
import { getErrorMessage } from "@/utils/getErrorMessage";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { queryClient } from "@/services/queryClient";
import { useAppSelector } from "@/store/hooks";
import { useNavigate } from "react-router-dom";

interface Props {
  mode: string;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
}

const calculateStrength = (value: string) => {
  let score = 0;
  if (value.length >= 8) score++;
  if (/[A-Z]/.test(value)) score++;
  if (/[a-z]/.test(value)) score++;
  if (/\d/.test(value)) score++;
  if (/[^A-Za-z0-9]/.test(value)) score++;
  return score;
};

const getStrengthLabel = (score: number) => {
  if (score <= 1) return "Very Weak";
  if (score === 2) return "Weak";
  if (score === 3) return "Fair";
  if (score === 4) return "Strong";
  return "Very Strong";
};

const getRequirements = (value: string) => [
  { label: "At least 8 characters", valid: value.length >= 8 },
  { label: "One uppercase letter", valid: /[A-Z]/.test(value) },
  { label: "One lowercase letter", valid: /[a-z]/.test(value) },
  { label: "One number", valid: /\d/.test(value) },
  { label: "One special character", valid: /[^A-Za-z0-9]/.test(value) },
];

const PasswordField = ({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) => {
  const [visible, setVisible] = useState(false);

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium">{label}</label>

      <div className="relative">
        <input
          type={visible ? "text" : "password"}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className="w-full h-14 rounded-xl border bg-background px-4 pr-12 outline-none focus:ring-2 focus:ring-primary"
        />

        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
        >
          {visible ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </div>
    </div>
  );
};

const TransactionPasswordSetting = ({ mode, open, setOpen }: Props) => {
  const { user } = useAppSelector((state) => state.auth);

  const navigate = useNavigate();

  const userId = user?.id || "";

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  /* reset on close */
  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
      }, 200);
    }
  }, [open]);

  const strength = useMemo(() => calculateStrength(newPassword), [newPassword]);
  const requirements = useMemo(
    () => getRequirements(newPassword),
    [newPassword],
  );

  const strong = requirements.every((r) => r.valid);
  const match = newPassword && newPassword === confirmPassword;

  const canSubmit =
    mode === "create"
      ? strong && match
      : currentPassword.trim().length > 0 && strong && match;

  const handleSubmit = async () => {
    if (!canSubmit || loading) return;

    try {
      setLoading(true);

      const payload =
        mode === "create"
          ? { securityCode: newPassword }
          : {
              currentSecurityCode: currentPassword,
              newSecurityCode: newPassword,
              confirmSecurityCode: confirmPassword,
            };

      const url =
        mode === "create"
          ? "/user/security-code/create"
          : "/user/security-code/update/me";

      const res = await makeRequest.put(url, payload);

      toast.success(res?.data?.message || "Success");

      queryClient.invalidateQueries({ queryKey: ["profile", userId] });

      setOpen(false);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="w-[92vw] max-w-md sm:max-w-lg rounded-2xl p-0 border shadow-lg bg-background">
        <div className="p-5 sm:p-6 space-y-5 max-h-[85vh] overflow-y-auto">
          {/* HEADER (FIXED) */}
          <DialogHeader className="text-center flex items-center flex-col space-y-2">
            <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <ShieldCheck className="w-10 h-10 text-primary" />
            </div>

            <DialogTitle className="text-lg sm:text-xl font-bold">
              {mode === "create"
                ? "Create Transaction Password"
                : "Update Transaction Password"}
            </DialogTitle>

            <DialogDescription className="text-xs sm:text-sm text-muted-foreground">
              Secure your account transactions with a transaction password.
            </DialogDescription>
          </DialogHeader>

          {/* CURRENT PASSWORD */}
          {mode === "update" && (
            <PasswordField
              label="Current Transaction Password"
              value={currentPassword}
              onChange={setCurrentPassword}
              placeholder="Enter current password"
            />
          )}

          {/* NEW PASSWORD */}
          <PasswordField
            label={
              mode === "create"
                ? "Transaction Password"
                : "New Transaction Password"
            }
            value={newPassword}
            onChange={setNewPassword}
            placeholder="Enter transaction password"
          />

          {/* STRENGTH */}
          {newPassword && (
            <div className="space-y-2">
              <Progress value={(strength / 5) * 100} />

              <p className="text-xs text-muted-foreground">
                Strength: <b>{getStrengthLabel(strength)}</b>
              </p>

              <div className="space-y-1">
                {requirements.map((r) => (
                  <div
                    key={r.label}
                    className="flex items-center gap-2 text-xs"
                  >
                    {r.valid ? (
                      <CheckCircle2 className="text-green-500 w-4 h-4" />
                    ) : (
                      <XCircle className="text-red-500 w-4 h-4" />
                    )}
                    {r.label}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* CONFIRM */}
          {strong && (
            <PasswordField
              label="Confirm Transaction Password"
              value={confirmPassword}
              onChange={setConfirmPassword}
              placeholder="Confirm transaction password"
            />
          )}

          {confirmPassword && !match && (
            <p className="text-xs text-red-500">
              Security passwords do not match
            </p>
          )}

          {mode === "update" && (
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                navigate("/setup/transaction-password/reset");
              }}
              className="w-full text-sm font-medium text-primary hover:underline"
            >
              Forgot Transaction Password?
            </button>
          )}

          {/* ACTION */}
          <Button
            disabled={!canSubmit || loading}
            onClick={handleSubmit}
            className="w-full h-11 rounded-xl"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : mode === "create" ? (
              "Create Transaction Password"
            ) : (
              "Update Transaction Password"
            )}
          </Button>

          {/* FOOTER NOTE */}
          <p className="text-[11px] text-center text-muted-foreground">
            Never share your transaction password with anyone.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TransactionPasswordSetting;
